import type { OpportunityPayload } from "@/lib/opportunityPayload";
import { buildOperatorTriagePacket } from "@/lib/operatorTriage";
import {
  APP_RESCUE_CONVERSION_GOAL,
  PUBLIC_CONVERSION_EXPERIMENT,
} from "@/lib/publicFunnelConstants";

export type OperatorPublicEntryPoint =
  | "build_ai_operator"
  | "audit_business_systems"
  | "rescue_finish_app"
  | "evaluate_domain_venture_deal"
  | "general_contact";

export type OperatorOpportunityUrgency = "low" | "medium" | "high" | "critical";

export interface OperatorPublicOpportunityRequest {
  entry_point: OperatorPublicEntryPoint;
  title?: string;
  summary?: string;
  contact: {
    name?: string;
    email: string;
    phone?: string;
  };
  organization_name?: string;
  estimated_value?: number;
  urgency?: OperatorOpportunityUrgency;
  source_url?: string;
  external_submission_id?: string;
  details?: Record<string, unknown>;
  scores?: Record<string, unknown>;
  consent: true;
  website?: string;
  company_stage?: string;
  timeline?: string;
  budget_range?: string;
  referrer?: string;
  website_url?: string;
  hp_company?: string;
}

export interface OperatorIntakeConfig {
  enabled: boolean;
  endpoint: string;
}

export type OperatorIntakeResult =
  | {
      mode: "local_preview";
      request: OperatorPublicOpportunityRequest;
      message: string;
    }
  | {
      mode: "submitted";
      request: OperatorPublicOpportunityRequest;
      response: unknown;
    };

export interface OperatorIntakeSubmittedResponse {
  opportunity_id?: string;
  status?: string;
  duplicate?: boolean;
}

export interface OperatorIntakeValidationResult {
  valid: boolean;
  errors: string[];
}

export function getOperatorIntakeConfig(
  env: Record<string, string | boolean | undefined> = import.meta.env
): OperatorIntakeConfig {
  const enabled = env.VITE_OPERATOR_INTAKE_ENABLED === "true" || env.VITE_OPERATOR_INTAKE_ENABLED === true;
  const endpoint = typeof env.VITE_OPERATOR_INTAKE_ENDPOINT === "string"
    ? env.VITE_OPERATOR_INTAKE_ENDPOINT
    : "/api/operator-intake";

  return {
    enabled,
    endpoint,
  };
}

export function validateOpportunityPayload(payload: OpportunityPayload): OperatorIntakeValidationResult {
  const errors: string[] = [];

  if (!payload.contact.name.trim()) {
    errors.push("Name is required.");
  }

  if (!payload.contact.email.trim()) {
    errors.push("Email is required.");
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.contact.email)) {
    errors.push("Enter a valid email address.");
  }

  if (!payload.request.summary.trim()) {
    errors.push("Summary is required.");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export function toOperatorOpportunityRequest(payload: OpportunityPayload): OperatorPublicOpportunityRequest {
  const titleSubject = payload.contact.company || payload.business.website || payload.contact.name || undefined;
  const summary = payload.request.summary.trim();
  const internalVerificationDetails = payload.context.internalVerification
    ? {
        internal_verification: true,
        test: true,
        verification: true,
        verification_label: payload.context.verificationLabel,
      }
    : {};

  const submissionId = payload.context.submissionId || payload.context.submittedAt;
  const urgency = normalizeUrgency(payload.request.urgency);
  const operatorTriage = buildOperatorTriagePacket(payload, urgency);

  return {
    entry_point: toOperatorEntryPoint(payload.entryPoint),
    title: titleSubject,
    summary,
    contact: {
      name: payload.contact.name || undefined,
      email: payload.contact.email,
      phone: payload.contact.phone || undefined,
    },
    organization_name: payload.contact.company || undefined,
    estimated_value: payload.qualification.estimatedValue ?? undefined,
    urgency,
    source_url: payload.context.pageUrl || undefined,
    external_submission_id: payload.context.internalVerification
      ? `internal-check:${payload.entryPoint}:${submissionId}`
      : `${payload.entryPoint}:${submissionId}`,
    consent: true,
    website: payload.business.website || undefined,
    company_stage: payload.business.companyStage || undefined,
    timeline: payload.request.timeline || undefined,
    budget_range: payload.request.budgetRange || undefined,
    referrer: payload.context.referrer || undefined,
    website_url: payload.business.website || undefined,
    hp_company: typeof payload.details.hp_company === "string" ? payload.details.hp_company : undefined,
    scores: {
      qualification_score: payload.qualification.score,
      estimated_value: payload.qualification.estimatedValue,
      signals: payload.qualification.signals,
    },
    details: {
      ...internalVerificationDetails,
      public_entry_point: payload.entryPoint,
      public_opportunity_type: payload.opportunityType,
      submitted_at: payload.context.submittedAt,
      consent_captured_at: payload.context.submittedAt,
      business: payload.business,
      request: payload.request,
      details: payload.details,
      ...(operatorTriage ? { operator_triage: operatorTriage } : {}),
      public_funnel: buildPublicFunnelSnapshot(payload),
      utm: payload.context.utm,
      boundary:
        "Domain data stays in the domain app. This payload contains business opportunity context for Operator triage.",
    },
  };
}

export async function submitOperatorOpportunity(
  payload: OpportunityPayload,
  config: OperatorIntakeConfig = getOperatorIntakeConfig(),
  fetcher: typeof fetch = fetch
): Promise<OperatorIntakeResult> {
  const validation = validateOpportunityPayload(payload);
  if (!validation.valid) {
    throw new Error(validation.errors.join(" "));
  }

  const request = toOperatorOpportunityRequest(payload);

  if (!config.enabled || !config.endpoint) {
    return {
      mode: "local_preview",
      request,
      message: "Operator intake is disabled or incomplete. No data was transmitted.",
    };
  }

  const headers: Record<string, string> = {
    "content-type": "application/json",
  };

  const response = await fetcher(config.endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(await readOperatorError(response));
  }

  return {
    mode: "submitted",
    request,
    response: await response.json().catch(() => null),
  };
}

export function asSubmittedResponse(response: unknown): OperatorIntakeSubmittedResponse | null {
  if (!response || typeof response !== "object") {
    return null;
  }

  const candidate = response as OperatorIntakeSubmittedResponse;
  return {
    opportunity_id: typeof candidate.opportunity_id === "string" ? candidate.opportunity_id : undefined,
    status: typeof candidate.status === "string" ? candidate.status : undefined,
    duplicate: typeof candidate.duplicate === "boolean" ? candidate.duplicate : undefined,
  };
}

async function readOperatorError(response: Response): Promise<string> {
  const fallback = "We could not send this intake right now. Please try again.";

  try {
    const body = await response.clone().json();
    if (body && typeof body === "object" && typeof body.error === "string" && body.error.trim()) {
      return body.error;
    }
  } catch {
    try {
      const text = await response.text();
      if (text.trim()) {
        return text.trim();
      }
    } catch {
      return fallback;
    }
  }

  return fallback;
}

function normalizeUrgency(value: string): OperatorOpportunityUrgency {
  const normalized = value.toLowerCase().trim();

  if (normalized.includes("critical")) return "critical";
  if (normalized.includes("high")) return "high";
  if (normalized.includes("low")) return "low";

  return "medium";
}

function toOperatorEntryPoint(entryPoint: string): OperatorPublicEntryPoint {
  if (entryPoint === "audit_business_systems") return "audit_business_systems";
  if (entryPoint === "rescue_finish_app") return "rescue_finish_app";
  // New builds share the existing app-work queue while retaining build_app in
  // public_entry_point metadata for routing and reporting inside Operator.
  if (entryPoint === "build_app") return "rescue_finish_app";
  if (entryPoint === "evaluate_domain_venture_deal") return "evaluate_domain_venture_deal";
  if (entryPoint === "build_ai_operator") return "build_ai_operator";
  if (entryPoint === "general_contact") return "general_contact";

  throw new Error("Choose a supported public SVM intake path before preparing Operator intake.");
}

function buildPublicFunnelSnapshot(payload: OpportunityPayload): Record<string, unknown> {
  const referrerHost = hostFromUrl(payload.context.referrer);
  const pagePath = pathFromUrl(payload.context.pageUrl);
  const utm = payload.context.utm;
  const hasUtm = Object.keys(utm).length > 0;
  const analyticsSignupType = payload.details.analyticsSignupType;

  return {
    experiment: PUBLIC_CONVERSION_EXPERIMENT,
    conversion_goal:
      analyticsSignupType === "updates"
        ? "updates_signup"
        : payload.details.signupType === "offer_interest"
        ? "offer_interest_signup"
        : payload.opportunityType === "APP_RESCUE"
          ? APP_RESCUE_CONVERSION_GOAL
          : "operator_opportunity_created",
    entry_point: payload.entryPoint,
    opportunity_type: payload.opportunityType,
    route: pagePath || payload.context.pageUrl || undefined,
    source_channel: inferSourceChannel(utm, referrerHost),
    has_utm: hasUtm,
    utm: hasUtm ? utm : undefined,
    referrer_host: referrerHost || undefined,
  };
}

function inferSourceChannel(utm: Record<string, string>, referrerHost: string): string {
  const source = utm.utm_source?.toLowerCase().trim();
  const medium = utm.utm_medium?.toLowerCase().trim();

  if (source) return medium ? `${medium}:${source}` : `utm:${source}`;
  if (!referrerHost) return "direct";
  if (referrerHost.endsWith("shockvaluemedia.com")) return "internal";
  if (/(google|bing|duckduckgo|yahoo|perplexity)/.test(referrerHost)) return "organic_search";
  if (/(linkedin|facebook|instagram|threads|x\.com|twitter|tiktok|youtube)/.test(referrerHost)) return "social_referral";

  return "referral";
}

function hostFromUrl(value: string): string {
  if (!value.trim()) return "";

  try {
    return new URL(value).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

function pathFromUrl(value: string): string {
  if (!value.trim()) return "";

  try {
    return new URL(value).pathname || "/";
  } catch {
    return "";
  }
}
